import { useDoctorAvailableSlots } from "@/hooks/useDoctorAvailableSlots";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AvailableSlotsGridProps {
  doctorId?: string;
  clinicId?: string;
  date?: Date;
  clinicName?: string;
  onSlotSelect: (startTime: string, endTime: string) => void;
  selectedSlot?: string; // HH:MM format
}

export const AvailableSlotsGrid = ({
  doctorId,
  clinicId,
  date,
  clinicName,
  onSlotSelect,
  selectedSlot,
}: AvailableSlotsGridProps) => {
  const { data: slots, isLoading } = useDoctorAvailableSlots(doctorId, clinicId, date);

  if (!doctorId || !clinicId || !date) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Available Slots at {clinicName || "This Clinic"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Available Slots at {clinicName || "This Clinic"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No available slots for this doctor at this clinic on the selected date.
            The doctor may be fully booked or not scheduled to work here today.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Available Slots at {clinicName || "This Clinic"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {slots.map((slot, index) => {
              const startTime = slot.slot_start.slice(0, 5); // HH:MM format
              
              // If slot is blocked, show as disabled with tooltip
              if (!slot.is_available) {
                return (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="bg-muted text-muted-foreground line-through opacity-60 cursor-not-allowed"
                      >
                        {startTime}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{slot.blocked_reason || "Booked"}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              
              // Available slot - clickable
              const isSelected = selectedSlot === startTime;
              return (
                <Button
                  key={index}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => onSlotSelect(slot.slot_start, slot.slot_end)}
                  className={isSelected 
                    ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2" 
                    : "hover:bg-primary hover:text-primary-foreground transition-colors"
                  }
                >
                  {startTime}
                </Button>
              );
            })}
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};
